import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { useUpcomingEvents } from "@/hooks/useUpcomingEvents";
import { AnimatedHeadline } from "@/components/shared/AnimatedHeadline";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { ChapterHeading } from "@/components/shared/ChapterHeading";
import { IconTile } from "@/components/shared/IconTile";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  WHO_ITS_FOR, PARTICIPATION_OFFERS, WANT_MORE_OF, STALL_HALL_FEATURES, FAQ_BOOKING,
  ADDONS, COLORS, BOOKSTALL_BANNER,
} from "@/data/siteData";

export default function BookAStall() {
  const { minPrice, maxPrice } = useUpcomingEvents();
  useSEO(
    "Book a Stall | The Market Place Jaipur",
    "Book a stall at The Market Place, Jaipur. A community to grow in — not just a stall to sell from."
  );
  const navigate = useNavigate();

  return (
    <div data-testid="book-a-stall-page">
      <section className="pt-16 pb-16 px-6 sm:px-8 max-w-4xl mx-auto text-center">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D81B60]">Book a Stall</span>
        </Reveal>
        <AnimatedHeadline
          lines={["A Community to Grow In."]}
          className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight"
          delayStart={0.1}
        />
        <Reveal delay={0.5}>
          <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            The Market Place gives homegrown businesses more than physical space. It gives them an opportunity to
            meet customers, build visibility and become part of a community that has been growing since 2019.
          </p>
          <button
            onClick={() => navigate("/vendor-booking")}
            data-testid="book-a-stall-apply-button"
            className="mt-8 rounded-full bg-[#D81B60] text-white px-8 py-4 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300"
          >
            Continue to booking
          </button>
        </Reveal>
      </section>

      {/* Photo banner */}
      <Reveal>
        <section className="px-6 sm:px-8 max-w-7xl mx-auto mb-8">
          <div className="rounded-3xl overflow-hidden">
            <img src={BOOKSTALL_BANNER} alt="Homegrown business founders at The Market Place, Jaipur" className="w-full h-[40vh] sm:h-[50vh] object-cover" />
          </div>
        </section>
      </Reveal>

      {/* Who this is for */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-5xl mx-auto">
        <ChapterHeading number="01" title="Who The Market Place Is For" subtitle="We welcome businesses at different stages of their journey. You may be:" />
        <RevealStagger className="grid sm:grid-cols-2 gap-4">
          {WHO_ITS_FOR.map((item, i) => (
            <RevealItem key={item}>
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${COLORS[i % COLORS.length]}18` }}>
                  <Check size={16} style={{ color: COLORS[i % COLORS.length] }} />
                </div>
                <p className="text-gray-700 font-medium">{item}</p>
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
        <Reveal><p className="text-center mt-8 text-gray-500 italic">What matters most is that you are building something you believe deserves to be discovered.</p></Reveal>
      </section>

      {/* What Participation Offers */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          <ChapterHeading number="02" title="What Participation Offers" />
          <RevealStagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PARTICIPATION_OFFERS.map((s, i) => (
              <RevealItem key={s.title}>
                <div className="rounded-2xl border border-gray-200 bg-white p-7 h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <IconTile icon={s.icon} title={s.title} desc={s.desc} color={COLORS[i % COLORS.length]} />
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Who We Want More Of */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-5xl mx-auto text-center">
        <Reveal>
          <ChapterHeading number="03" title="Who We Want More Of" subtitle="The Market Place is actively looking to welcome more:" center />
        </Reveal>
        <RevealStagger className="flex flex-wrap justify-center gap-3 -mt-4">
          {WANT_MORE_OF.map((item, i) => (
            <RevealItem key={item}>
              <span className="inline-block rounded-full px-5 py-2.5 text-sm font-semibold" style={{ backgroundColor: `${COLORS[i % COLORS.length]}15`, color: COLORS[i % COLORS.length] }}>
                {item}
              </span>
            </RevealItem>
          ))}
        </RevealStagger>
        <Reveal><p className="mt-8 text-gray-500 italic">We are interested in businesses that need a community to grow, not simply a stall to sell.</p></Reveal>
      </section>

      {/* Stall Inclusions & Pricing */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 bg-[#FDFBF7]">
        <div className="max-w-5xl mx-auto">
          <ChapterHeading number="04" title="Stall Inclusions & Pricing" subtitle={minPrice === maxPrice ? `₹${minPrice.toLocaleString("en-IN")} per stall, per day — simple, transparent pricing.` : `₹${minPrice.toLocaleString("en-IN")}–₹${maxPrice.toLocaleString("en-IN")} per stall, per day, depending on the market date.`} />
          <RevealStagger className="grid sm:grid-cols-3 gap-5 mb-10">
            {STALL_HALL_FEATURES.map((f, i) => (
              <RevealItem key={f.title}>
                <div className="rounded-2xl border border-gray-200 bg-white p-6 h-full text-center">
                  <IconTile icon={f.icon} title={f.title} color={COLORS[i % COLORS.length]} center />
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
          <Reveal>
            <div className="rounded-2xl border-2 border-[#D81B60]/30 bg-white p-8 text-center mb-6">
              <p className="font-mono text-xs uppercase tracking-[0.15em] text-gray-400">Per Stall, Per Day</p>
              <p className="font-extrabold text-4xl mt-2 text-[#D81B60]" data-testid="dynamic-price-display">
                {minPrice === maxPrice ? `₹${minPrice.toLocaleString("en-IN")}` : `₹${minPrice.toLocaleString("en-IN")}–₹${maxPrice.toLocaleString("en-IN")}`}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl bg-white border border-gray-200 p-5 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">{ADDONS.electricity.label} (optional add-on)</span>
                <span className="font-bold text-[#E8A33D]">₹{ADDONS.electricity.price}</span>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 p-5 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">{ADDONS.extraTable.label} (optional add-on)</span>
                <span className="font-bold text-[#3F51B5]">₹{ADDONS.extraTable.price}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-3xl mx-auto">
        <ChapterHeading number="05" title="FAQ" />
        <Accordion type="single" collapsible data-testid="booking-faq-accordion">
          {FAQ_BOOKING.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`booking-faq-item-${i}`}>
              <AccordionTrigger className="font-bold text-lg text-left">{f.q}</AccordionTrigger>
              <AccordionContent className="text-gray-500">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Closing */}
      <section className="pb-16 px-6 sm:px-8 max-w-3xl mx-auto text-center">
        <Reveal>
          <h2 className="font-extrabold text-3xl sm:text-4xl leading-tight mb-4">Your Business May Start Small. Its Opportunity Shouldn't.</h2>
          <p className="text-gray-500 mb-8">Bring what you are building to a community that believes homegrown businesses deserve to be seen.</p>
          <button
            onClick={() => navigate("/vendor-booking")}
            data-testid="book-a-stall-bottom-apply-button"
            className="rounded-full bg-[#D81B60] text-white px-10 py-4 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300"
          >
            Continue to booking
          </button>
        </Reveal>
      </section>
    </div>
  );
}
